import type { ConnectorDefinition } from "../types";

/**
 * Smartsheet tools that only READ (access scopes `READ_SHEETS` / `READ_SIGHTS`).
 * Always available once the connector is live; never gated by the write flag.
 * `list_shares` is included here — it lists shares but mutates nothing.
 */
export const SMARTSHEET_READ_TOOLS = [
  "search",
  "browse_workspace",
  "browse_folder",
  "list_workspaces",
  "get_sheet_summary",
  "get_sheet_version",
  "find_in_sheet",
  "get_columns",
  "get_cell_history",
  "list_attachments",
  "list_row_attachments",
  "list_discussion_attachments",
  "get_attachment",
  "list_sheet_discussions",
  "list_row_discussions",
  "get_discussion",
  "list_reports",
  "get_report",
  "list_dashboards",
  "get_dashboard",
  "list_shares",
  "list_favorites",
  "is_favorite",
] as const;

/**
 * Smartsheet tools that WRITE / mutate (access scopes `WRITE_SHEETS`,
 * `CREATE_SHEETS`, `ADMIN_SHEETS`, `ADMIN_WORKSPACES`). These are gated behind
 * `connectors.smartsheet.write`: with the write flag off they never materialize
 * for the model (filtered in `defineMcpCapability.resolveTools`), so the
 * connector ships read-only first. Imported by the Smartsheet capability so the
 * connector definition stays the single source of truth for the split.
 *
 * The same list drives HITL: the capability declares an approval for every tool
 * here (ADR-050), so a write the user hasn't trusted prompts before it runs.
 * Adding a name here without a label there is a compile error.
 *
 * Additive and in-place edits only — Smartsheet's destructive tools are
 * excluded from the connector outright (see {@link SMARTSHEET_EXCLUDED_TOOLS}).
 */
export const SMARTSHEET_WRITE_TOOLS = [
  "create_sheet",
  "add_columns",
  "update_column",
  "add_rows",
  "update_rows",
  "attach_url",
  "create_discussion_on_row",
  "create_discussion_on_sheet",
  "add_comment",
  "update_comment",
  "create_workspace",
  "create_folder_in_workspace",
  "create_folder_in_folder",
  "add_favorites",
  "delete_favorites",
] as const;

/**
 * Smartsheet MCP tools deliberately NOT wired up: everything that destroys
 * content. They're absent from `toolAllowlist`, so `discoverMcpTools` drops
 * them from the catalog and they never reach the capability, the model's tool
 * set, or the progressive-disclosure manifest — no flag or approval can turn
 * them on. A user who wants something deleted does it in Smartsheet.
 *
 * The rationale is the blast radius rather than the intent: Smartsheet rows and
 * columns carry other people's work, deletes cascade (a column takes its cells,
 * a discussion takes its comments), and the MCP surface offers no undo, so a
 * misread instruction is unrecoverable from Magnus's side. Every remaining
 * write either adds something or edits a value in place.
 *
 * `delete_favorites` is intentionally still allowed — it un-stars a sheet
 * rather than deleting anything, and it's the undo for `add_favorites`.
 *
 * Listed (not simply deleted) so the exclusion survives someone regenerating
 * this file from Smartsheet's tool docs; a test asserts the two sets stay
 * disjoint.
 */
export const SMARTSHEET_EXCLUDED_TOOLS = [
  "delete_column",
  "delete_rows",
  "delete_attachment",
  "delete_discussion",
  "delete_comment",
] as const;

export const smartsheetConnector: ConnectorDefinition = {
  id: "smartsheet",
  name: "Smartsheet",
  description: "Read and update your Smartsheet sheets, rows, and reports",
  icon: "smartsheet",
  authType: "apikey",
  kind: "mcp",
  apiKey: {
    fields: [
      {
        name: "apiToken",
        label: "API token",
        type: "password",
        required: true,
        placeholder: "Paste your Smartsheet API token",
        helpText:
          "Generate a token in Smartsheet under Account → Personal Settings → API Access.",
      },
    ],
  },
  mcp: {
    serverUrl: "https://mcp.smartsheet.com",
    transport: "http",
    toolAllowlist: [...SMARTSHEET_READ_TOOLS, ...SMARTSHEET_WRITE_TOOLS],
    writeToolNames: [...SMARTSHEET_WRITE_TOOLS],
  },
  isEnabled: () => true,
};
